import { useCallback, useEffect, useRef, useState } from 'react';

/** Longest gap between dragover events while a drag hovers (browsers repeat them every 350ms ± 200ms or faster). */
const DRAG_IDLE_MS = 1000;

/**
 * Whether a file drag is over an element, for drop overlays that must never
 * stay on. Enter, leave and drop alone miss drags that end somewhere else: a
 * drop that another handler stops (the Partnr panel), a drag that leaves the
 * window, or one cancelled in another app. So the state also clears when
 * dragover events stop arriving, and right after any drop or dragend in the
 * window (heard in the capture phase, so a handler that stops the event
 * can't hide it).
 *
 * Call `touch` from dragenter and dragover, `clear` from a real leave and from drop.
 */
export function useFileDragOver() {
  const [over, setOver] = useState(false);
  const idle = useRef<ReturnType<typeof setTimeout>>();

  const clear = useCallback(() => {
    clearTimeout(idle.current);
    setOver(false);
  }, []);

  const touch = useCallback(() => {
    setOver(true);
    clearTimeout(idle.current);
    idle.current = setTimeout(() => setOver(false), DRAG_IDLE_MS);
  }, []);

  useEffect(() => {
    if (!over) return;
    // Just after, not during: clearing mid-drop unmounts the overlay the drop is landing on,
    // and React then ignores the drop (the upload never starts)
    const clearSoon = () => setTimeout(clear, 0);
    window.addEventListener('drop', clearSoon, true);
    window.addEventListener('dragend', clearSoon, true);
    return () => {
      window.removeEventListener('drop', clearSoon, true);
      window.removeEventListener('dragend', clearSoon, true);
    };
  }, [over, clear]);

  useEffect(() => () => clearTimeout(idle.current), []);

  return { over, touch, clear };
}
